import React, { useState } from 'react';
import { CloseIconWhite } from '@/components/icons/icons';
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Stack,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';
import styledEmotion from '@emotion/styled';
import { config } from '@/config/environment';

interface LearnMoreModalProps {
  open: boolean;
  onClose: () => void;
}

const DialogTitle = styled(Typography)``;

const TabsEllipse = styledEmotion('div')`
  display: flex;
  align-items: center;
  justify-content: stretch;
  width: 100%;
  padding: 8px; /* small padding around the buttons */
  gap: 10px;
  border-radius: 9999px;
  background-color: rgba(198, 153, 187, 0.2);
  background-image: radial-gradient(circle at 20% 50%, rgba(198, 153, 187, 0.2), transparent 40%),
    radial-gradient(circle at 80% 50%, rgba(198, 153, 187, 0.1), transparent 60%);
`;

const TabButton = styledEmotion('button')<{ active?: boolean }>`
  flex: 1; /* fill remaining space equally */
  background: ${p => (p.active ? '#FFCF31' : 'rgba(198, 153, 187,0.2)')};
  border: none;
  padding: 10px 20px;
  margin: 0;
  border-radius: 999px;
  cursor: pointer;
  font-size: 22px;
  font-family: var(--font-work-sans), sans-serif;
  font-weight: 500;
  color: ${p => (p.active ? '#472b4a' : 'white')};
  transition: transform 200ms ease, box-shadow 200ms ease;
  box-shadow: ${p => (p.active ? '0 6px 18px rgba(0,0,0,0.18)' : 'none')};
  white-space: normal;
  line-height: 1.2;

  &:hover {
    transform: translateY(-3px);
  }

  @media (max-width: 600px) {
    font-size: 16px;
    padding: 10px 12px;
  }
`;

function TabPanel({
  children,
  value,
  index,
}: {
  children?: React.ReactNode;
  value: number;
  index: number;
}) {
  return (
    <div hidden={value !== index}>{value === index && <Box sx={{ p: 3 }}>{children}</Box>}</div>
  );
}

export const LearnMoreModal: React.FC<LearnMoreModalProps> = ({ open, onClose }) => {
  const [tab, setTab] = useState(0);
  const isMobile = useMediaQuery('(max-width:600px)');

  const handleChange = (_: React.SyntheticEvent, newValue: number) => setTab(newValue);

  const archiveMode = config.platformMode === 'archive';

  const tabs = archiveMode
    ? ['What was coaching?', 'Why was it important?', 'Who was it for?']
    : ["What's coaching", 'Why is it important?', "Who's it for?"];

  const content = archiveMode
    ? [
        {
          title: 'What was coaching?',
          body: `Coaching offered space to explore your habits, desires, and the changes you wanted to make.

We asked insight-raising questions to help you see patterns, possibilities, and new ways forward, which could be hard to spot on your own. Together, we co-created tools and strategies that helped you have fewer of the moments you didn’t want and more of the ones you did.

We were certified life coaches with 10+ years of experience supporting all kinds of humans who moved through the world in different ways. Sessions were flexible, one-on-one or in small groups, online or in person, and always free.`,
          img: '/discover/coaching/whats-coaching.png',
        },
        {
          title: 'Why was it important?',
          body: `Curiko Coaches weren’t focused on big, rigid goals. We cared about small shifts—the tiny moments where something clicked, you tried a new approach, or noticed yourself growing. Those moments added up to a flourishing life.

Growth wasn’t always comfortable: trying new things, making mistakes, and stepping outside your usual patterns could feel awkward or messy. Coaching gave you a place to show up as you were while stretching into who you wanted to be.

Coaching supported the in-between parts of growth: the uncertain steps, experiments, and the things that don’t go as planned. It was a space to use your strengths, get curious, and keep moving forward—even when it felt uncomfortable.`,
          img: '/discover/coaching/why-its-important.png',
        },
        {
          title: 'Who was it for?',
          body: `Whether you were feeling stuck or on a roll, coaching was for anyone who wanted some encouragement, challenge, reframing, or inspiration!

Coaching offered a space to grow and explore what was possible. We worked with adults of all abilities, including those with cognitive or developmental differences.`,
          img: '/discover/coaching/whos-it-for.png',
        },
      ]
    : [
        {
          title: "What's coaching",
          body: `Coaching offers space to explore your habits, desires, and the changes you want to make.

We ask insight-raising questions to help you see patterns, possibilities, and new ways forward, which can be hard to spot on your own. Together, we co-create tools and strategies that help you have fewer of the moments you don’t want and more of the ones you do.

We are certified life coaches with 10+ years experience supporting all kinds of humans who move through the world in different ways. Sessions are flexible, one-on-one or in small groups, online or in person, and always free.`,
          img: '/discover/coaching/whats-coaching.png',
        },
        {
          title: 'Why is it important?',
          body: `Curiko Coaches aren’t focused on big, rigid goals. We care about small shifts—the tiny moments where something clicks, you try a new approach, or notice yourself growing. Those moments add up to a flourishing life.

Growth isn’t always comfortable: trying new things, making mistakes, and stepping outside your usual patterns can feel awkward or messy. Coaching gives you a place to show up as you are while stretching into who you want to be.

Coaching supports the in-between parts of growth: the uncertain steps, experiments, and the things that don’t go as planned. It’s a space to use your strengths, get curious, and keep moving forward—even when it feels uncomfortable.`,
          img: '/discover/coaching/why-its-important.png',
        },
        {
          title: "Who's it for?",
          body: `Whether you are feeling stuck, or on a roll, Coaching is for anyone who wants some encouragement, challenge, reframing, or inspiration! 

Do you want a space to grow and explore what's possible? Come to coaching! We work with adults of all abilities, including those with cognitive or developmental differences.`,
          img: '/discover/coaching/whos-it-for.png',
        },
      ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: theme => theme.palette.common.midnight,
          color: 'white',
          opacity: 0.97,
          ...(isMobile
            ? {
                borderRadius: 0,
                m: 0,
              }
            : null),
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 3, pt: 2 }}
      >
        <DialogTitle variant="h4" color="white"></DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIconWhite />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2, mt: 1 }}>
        <TabsEllipse>
          <TabButton active={tab === 0} onClick={() => setTab(0)} aria-pressed={tab === 0}>
            {tabs[0]}
          </TabButton>
          <TabButton active={tab === 1} onClick={() => setTab(1)} aria-pressed={tab === 1}>
            {tabs[1]}
          </TabButton>
          <TabButton active={tab === 2} onClick={() => setTab(2)} aria-pressed={tab === 2}>
            {tabs[2]}
          </TabButton>
        </TabsEllipse>
      </Box>

      <Box
        sx={{
          px: 2,
          pb: 3,
          ...(isMobile
            ? {
                flex: 1,
                overflowY: 'auto',
              }
            : null),
        }}
      >
        {content.map((c, i) => (
          <TabPanel key={c.title} value={tab} index={i}>
            <Grid container spacing={2} sx={{ height: { xs: 'auto', sm: 360 } }}>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    height: { xs: 220, sm: '100%' },
                    width: '100%',
                    backgroundImage: `url(${c.img})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: 5,
                    borderColor: '#C699BB',
                    borderWidth: 1.5,
                    borderStyle: 'solid',
                  }}
                  role="img"
                  aria-label={c.title}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                {String(c.body)
                  .split(/\n\s*\n/)
                  .map((para, idx) => (
                    <Typography
                      key={idx}
                      variant="body1"
                      paragraph
                      sx={{
                        fontSize: '16px',
                        fontFamily: 'var(--font-work-sans), sans-serif',
                        color: 'rgba(255,255,255,0.9)',
                        lineHeight: 1.6,
                        paddingLeft: { xs: 0, sm: '30px' },
                      }}
                    >
                      {para.trim()}
                    </Typography>
                  ))}
              </Grid>
            </Grid>
          </TabPanel>
        ))}
      </Box>
    </Dialog>
  );
};

export default LearnMoreModal;
